import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import PatientPortal from "@/components/PatientPortal";
import DoctorPortal from "@/components/DoctorPortal";
import EnterprisePortal from "@/components/EnterprisePortal";
import FamilyPortal from "@/components/FamilyPortal";
import SathiPortal from "@/components/SathiPortal";
import Logo from "@/components/Logo";
import AddressCard from "@/components/AddressCard";
import {
  LogOut, Video, Calendar, Activity, Users, Clock, ShieldCheck, HeartPulse, Bed, CalendarClock, Heart, Siren, ClipboardCheck, LayoutGrid,
} from "lucide-react";

const easeOut = [0.16, 1, 0.3, 1];

function StatCard({ icon: Icon, label, value, i }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, ease: easeOut }}
      className="bg-white rounded-2xl border border-slate-200 p-6">
      <div className="w-11 h-11 rounded-xl bg-[#041E26] flex items-center justify-center mb-4"><Icon className="w-5 h-5 text-[#F59E0B]" /></div>
      <div className="font-head text-3xl font-semibold text-[#041E26]">{value}</div>
      <div className="text-slate-500 text-sm mt-1">{label}</div>
    </motion.div>
  );
}

const STAT_CONFIG = {
  patient: [
    { icon: Video, label: "My appointments", key: "my_appointments" },
    { icon: CalendarClock, label: "Upcoming", key: "upcoming" },
    { icon: HeartPulse, label: "Care plan", key: "care_plan" },
    { icon: Activity, label: "Vitals synced", key: "vitals_synced" },
  ],
  doctor: [
    { icon: Users, label: "Appointments", key: "appointments" },
    { icon: Clock, label: "Waiting now", key: "waiting_now" },
    { icon: Calendar, label: "Open slots", key: "open_slots" },
    { icon: ShieldCheck, label: "Verified", key: "verified" },
  ],
  therapist: [
    { icon: Users, label: "Sessions", key: "appointments" },
    { icon: Clock, label: "Waiting now", key: "waiting_now" },
    { icon: Calendar, label: "Open slots", key: "open_slots" },
    { icon: ShieldCheck, label: "Verified", key: "verified" },
  ],
  enterprise: [
    { icon: Video, label: "Total appointments", key: "total_appointments" },
    { icon: Activity, label: "Active now", key: "active_now" },
    { icon: Users, label: "Verified providers", key: "verified_providers" },
    { icon: Bed, label: "Pending verifications", key: "pending_verifications" },
  ],
  family: [
    { icon: Heart, label: "Enrolled elders", key: "enrolled_elders" },
    { icon: ShieldCheck, label: "Active plans", key: "active_plans" },
    { icon: CalendarClock, label: "Total plans", key: "total_plans" },
    { icon: Siren, label: "SOS alerts", key: "sos_alerts" },
  ],
  sathi: [
    { icon: Users, label: "Assigned elders", key: "assigned_elders" },
    { icon: ClipboardCheck, label: "Tasks done today", key: "tasks_done_today" },
    { icon: Activity, label: "On duty", key: "on_duty" },
    { icon: Clock, label: "Shift", key: "shift" },
  ],
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const fetchStats = () => api.get("/dashboard/stats").then(({ data }) => setStats(data.stats)).catch(() => {});
    fetchStats();
    const t = setInterval(fetchStats, 8000);
    api.get("/admin/whoami").then(({ data }) => setIsAdmin(!!data.is_admin)).catch(() => {});
    return () => clearInterval(t);
  }, []);

  const roleLabel = { patient: "Patient / Member", doctor: "Doctor / Provider", therapist: "Therapist / Mental Health Pro", enterprise: "Enterprise / Hospital Partner", family: "Family (Eldercare)", sathi: "Caregiver (Sathi)" }[user.role];
  const statConfig = STAT_CONFIG[user.role];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="bg-[#041E26] px-6 lg:px-10 py-5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button data-testid="dash-home-btn" onClick={() => navigate("/")} className="flex items-center gap-2">
            <Logo className="h-10" onDark showTagline />
          </button>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-white text-sm font-semibold">{user.name}</div>
              <div className="text-white/40 text-xs">{roleLabel}</div>
            </div>
            {isAdmin && (
              <Button data-testid="dash-admin-btn" onClick={() => navigate("/admin")}
                className="rounded-full gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#041E26] font-semibold">
                <LayoutGrid className="w-4 h-4" /> Admin Panel
              </Button>
            )}
            <Button data-testid="dash-logout-btn" onClick={() => { logout(); navigate("/"); }} variant="outline"
              className="rounded-full gap-2 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white"><LogOut className="w-4 h-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-head text-3xl md:text-4xl font-light text-[#041E26] tracking-tight">
            Welcome back, <span className="text-[#D97706] font-normal">{user.name.split(" ")[0]}</span>
          </h1>
          <p className="text-slate-500 mt-1">Your {user.role === "enterprise" ? "network" : "care"} overview.</p>
        </motion.div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8 mb-8">
          {stats && statConfig.map((c, i) => <StatCard key={c.key} icon={c.icon} label={c.label} value={String(stats[c.key])} i={i} />)}
        </div>

        <div className="mb-12">
          <AddressCard title={
            user.role === "doctor" || user.role === "therapist" ? "Clinic / Practice Address"
            : user.role === "enterprise" ? "Hospital / Organisation Address"
            : user.role === "sathi" ? "My Address (visible to family)"
            : "My Address"
          } />
        </div>

        {user.role === "patient" && <PatientPortal />}
        {(user.role === "doctor" || user.role === "therapist") && <DoctorPortal />}
        {user.role === "enterprise" && <EnterprisePortal />}
        {user.role === "family" && <FamilyPortal />}
        {user.role === "sathi" && <SathiPortal />}
      </main>
    </div>
  );
}
